import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { allBusinessOptions, getRandomBusinessOptions, getRandomQuickOptions, createQuickOptions } from '../data/optionsData';

const ROTATION_INTERVAL_MS = 45000; // 45 seconds - stable, slow rotation
const ROTATION_ANIMATION_MS = 2500;

export const useBusinessLogic = () => {
  const { isAdmin, isAuthenticated, updateProfile } = useAuth();
  const { t } = useLanguage();
  
  // Business state
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [randomBusinessOptions, setRandomBusinessOptions] = useState([]);
  const [changingOptions, setChangingOptions] = useState(new Set());
  const [changingBusinessOptions, setChangingBusinessOptions] = useState(new Set());
  
  // Quick options state - use translated options
  const allQuickOptionsTranslated = useMemo(() => createQuickOptions(t) || [], [t]);
  const [quickOptions, setQuickOptions] = useState([]);
  const quickOptionsRef = useRef(quickOptions);
  quickOptionsRef.current = quickOptions;
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;
  
  // Scroll state for modals
  const [isAtBottom, setIsAtBottom] = useState(false);
  
  // Initialize business options
  useEffect(() => {
    setRandomBusinessOptions(getRandomBusinessOptions());
  }, []);
  
  // Initialize quick options once when translated list is ready (and when language changes)
  useEffect(() => {
    if (!Array.isArray(allQuickOptionsTranslated) || allQuickOptionsTranslated.length === 0) return;
    const filtered = getRandomQuickOptions(allQuickOptionsTranslated, false, true).filter(option => {
      if (!isAdminRef.current()) {
        return option.label !== 'Analytics' && option.label !== 'User Management';
      }
      return true;
    });
    setQuickOptions(filtered);
  }, [allQuickOptionsTranslated]);
  
  // Safe JSON parse for fetch responses (avoids "did not match the expected pattern" when server returns HTML/empty)
  const safeParseJson = async (response) => {
    const text = await response.text();
    if (!text || !text.trim()) return null;
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  // Fetch user businesses from database
  const fetchUserBusinesses = useCallback(async () => {
    try {
      if (!isAuthenticated()) return;
      
      setLoadingBusinesses(true);
      const response = await fetch('/api/users/businesses', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const businessData = await safeParseJson(response);
        setUserBusinesses(businessData?.businesses ?? []);
      } else {
        // Fallback: try to get from user profile
        const profileResponse = await fetch('/api/users/profile', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (profileResponse.ok) {
          const userData = await safeParseJson(profileResponse);
          if (userData) {
            const businesses = [];
            if (userData.business_type) {
              businesses.push({
                id: 1,
                type: userData.business_type,
                is_primary: true
              });
            }
            if (userData.second_business_type) {
              businesses.push({
                id: 2,
                type: userData.second_business_type,
                is_primary: false
              });
            }
            setUserBusinesses(businesses);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user businesses:', error);
      // Fallback to localStorage data
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        const businesses = [];
        if (userData.business_type) {
          businesses.push({
            id: 1,
            type: userData.business_type,
            is_primary: true
          });
        }
        if (userData.second_business_type) {
          businesses.push({
            id: 2,
            type: userData.second_business_type,
            is_primary: false
          });
        }
        setUserBusinesses(businesses);
      }
    } finally {
      setLoadingBusinesses(false);
    }
  }, [isAuthenticated]);
  
  // Single slow rotation timer: change one random card every ROTATION_INTERVAL_MS. Uses refs so interval is stable.
  useEffect(() => {
    const pool = Array.isArray(allQuickOptionsTranslated) ? allQuickOptionsTranslated : [];
    if (pool.length < 2) return;
    
    const interval = setInterval(() => {
      const current = quickOptionsRef.current;
      if (!current || current.length === 0) return;
      const randomIndex = Math.floor(Math.random() * current.length);
      setChangingOptions(prev => new Set([...prev, randomIndex]));
      
      setTimeout(() => {
        const newOptions = [...quickOptionsRef.current];
        const availableOptions = pool.filter(opt => 
          !newOptions.some(existing => existing.key === opt.key || existing.label === opt.label)
        );
        if (availableOptions.length > 0) {
          const randomNewOption = availableOptions[Math.floor(Math.random() * availableOptions.length)];
          newOptions[randomIndex] = randomNewOption;
          const filtered = newOptions.filter(option => {
            if (!isAdminRef.current()) {
              return option.label !== 'Analytics' && option.label !== 'User Management';
            }
            return true;
          });
          setQuickOptions(filtered);
        }
        setChangingOptions(prev => {
          const next = new Set(prev);
          next.delete(randomIndex);
          return next;
        });
      }, ROTATION_ANIMATION_MS);
    }, ROTATION_INTERVAL_MS);
    
    return () => clearInterval(interval);
  }, [allQuickOptionsTranslated]);
  
  // Timer to change business type options individually (same slow interval)
  const randomBusinessOptionsRef = useRef(randomBusinessOptions);
  randomBusinessOptionsRef.current = randomBusinessOptions;
  useEffect(() => {
    const interval = setInterval(() => {
      const current = randomBusinessOptionsRef.current;
      if (!current || current.length === 0) return;
      const randomIndex = Math.floor(Math.random() * current.length);
      setChangingBusinessOptions(prev => new Set([...prev, randomIndex]));
      setTimeout(() => {
        const newOptions = [...randomBusinessOptionsRef.current];
        const availableOptions = allBusinessOptions.filter(option => 
          !newOptions.some(existing => existing.type === option.type)
        );
        if (availableOptions.length > 0) {
          const randomNewOption = availableOptions[Math.floor(Math.random() * availableOptions.length)];
          newOptions[randomIndex] = randomNewOption;
          setRandomBusinessOptions(newOptions);
        }
        setChangingBusinessOptions(prev => {
          const next = new Set(prev);
          next.delete(randomIndex);
          return next;
        });
      }, ROTATION_ANIMATION_MS);
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);
  
  // Scroll functions for modals
  const scrollBusinessModal = (direction) => {
    const modalContent = document.querySelector('.business-modal-content');
    if (!modalContent) return;
    
    const scrollAmount = 300;
    const currentScroll = modalContent.scrollTop;
    const newScroll = direction === 'down' 
      ? currentScroll + scrollAmount 
      : currentScroll - scrollAmount;
    
    modalContent.scrollTo({
      top: newScroll,
      behavior: 'smooth'
    });
  };


  
  return {
    // State
    userBusinesses,
    loadingBusinesses,
    randomBusinessOptions,
    changingOptions,
    changingBusinessOptions,
    quickOptions,
    allQuickOptions: allQuickOptionsTranslated,
    isAtBottom,
    
    // Functions
    fetchUserBusinesses,
    scrollBusinessModal,
    setQuickOptions
  };
};
